import React from 'react';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { BEACON_CHART_SPACING } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import AtlasV2OrganicTrafficV2OrganicTrafficSplineChart from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2OrganicTrafficV2/AtlasV2OrganicTrafficV2OrganicTrafficSplineChart';
import AtlasV2OrganicTrafficV2OrganicKeywordCountSplineChart from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2OrganicTrafficV2/AtlasV2OrganicTrafficV2OrganicKeywordCountSplineChart';
import AtlasV2OrganicTrafficV2ShareOfTrafficSplineChart from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2OrganicTrafficV2/AtlasV2OrganicTrafficV2ShareOfTrafficSplineChart';
import AtlasV2TrafficDropdownTopEntityTraffic from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2TotalTrafficV2/AtlasV2TrafficDropdownTopEntityTraffic';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { useBrandEntity, useBusinessUnitEntity, useProductEntity } from 'src/utils/Hooks/reduxSelectors';
import { useSegmentEntity } from 'src/utils/Hooks/reduxSelectors';
import AtlasV2TotalFilterableStackedBarChart from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2TotalTrafficV2/AtlasV2TotalFilterableStackedBarChart';
import { useCategoryEntity, useSubcategoryEntity } from 'src/utils/Hooks/reduxSelectors';
import { AtlasV2TrafficProductGrid } from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2TotalTrafficV2/AtlasV2TrafficV2ProductGrid';
import AtlasV2OrganicTrafficDoubleDropdownTopEntity from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2OrganicTrafficV2/AtlasV2OrganicTrafficDoubleDropdownTopEntity';
import AtlasV2FilterableStackedBarChart from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2TotalTrafficV2/AtlasV2TotalFilterableStackedBarChart';
import AtlasV2TotalTrafficV2BrandedUnbrandedSplineChart from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2TotalTrafficV2/AtlasV2TrafficV2BrandedUnbrandedSplineChart';
import { shouldShowBrandUnbranded } from 'src/utils/app';
import { useAtlasV2TabTreatmentRedirect } from 'src/components/AtlasV2Components/Hooks/useAtlasV2TabTreatmentRedirect';

/**
 * Atlas V2 Organic Traffic V2 Page layout - Tab name: traffic subtab name: organicClicks
 */
export const AtlasV2OrganicTrafficV2PageLayout = () => {
  const { isCategoryEntity, isAllCategoriesEntity } = useCategoryEntity();
  const { isSubcategoryEntity } = useSubcategoryEntity();
  const { isSegmentEntity } = useSegmentEntity();
  const { isBusinessUnitEntity } = useBusinessUnitEntity();
  const { isBrandEntity } = useBrandEntity();
  const { isProductEntity } = useProductEntity();
  const { isRedirecting } = useAtlasV2TabTreatmentRedirect();
  if (isRedirecting) return null;

  return (
    <BasePageContainer>
      <Flex flexDirection="column" gap={BEACON_CHART_SPACING}>
        <AtlasV2OrganicTrafficV2OrganicTrafficSplineChart />

        <AtlasV2OrganicTrafficV2ShareOfTrafficSplineChart />

        {!isCategoryEntity && !isSubcategoryEntity && !isSegmentEntity && !isBusinessUnitEntity && (
          <AtlasV2TrafficDropdownTopEntityTraffic
            groupByFields={[{ id: 'keyword', label: 'Keyword', indexName: 'traffic' }]}
            queryKey="organicClicks_singleDropdown"
            fields={[
              {
                fieldName: 'organicClicks',
                label: 'Organic Traffic',
                metricType: 'organicClicks',
                metricTypeDisplay: METRICTYPE.VOLUME
              },
              {
                fieldName: 'organicClicksShare',
                label: 'Share of Organic Traffic',
                metricType: 'organicClicks',
                metricTypeDisplay: METRICTYPE.PERCENT,
                isMarketShare: true
              }
            ]}
          />
        )}

        {(isCategoryEntity || isSubcategoryEntity || isSegmentEntity || isBusinessUnitEntity) && (
          <AtlasV2OrganicTrafficDoubleDropdownTopEntity />
        )}

        {(isCategoryEntity || isSubcategoryEntity || isSegmentEntity || isBusinessUnitEntity) && (
          <AtlasV2TotalFilterableStackedBarChart
            metricDropdownOptions={[
              {
                id: 'organicTraffic',
                label: 'Organic Traffic',
                apiMetricFieldName: 'organicClicks',
                subtitleLabel: 'organic traffic'
              },
              {
                id: 'percentTraffic',
                label: '% of Organic  Traffic',
                apiMetricFieldName: 'organicClicks',
                isPercentageVisible: true,
                defaultMetric: 'organicTraffic',
                subtitleLabel: 'percentage of organic traffic'
              }
            ]}
            groupByFieldOptions={[
              {
                id: 'keyword',
                label: 'Keyword'
              }
            ]}
          />
        )}

        <AtlasV2OrganicTrafficV2OrganicKeywordCountSplineChart />

        {!isCategoryEntity &&
          !isSubcategoryEntity &&
          !isSegmentEntity &&
          !isBusinessUnitEntity &&
          !isProductEntity &&
          !isBrandEntity && <AtlasV2OrganicTrafficDoubleDropdownTopEntity />}

        {isBrandEntity && (
          <AtlasV2OrganicTrafficDoubleDropdownTopEntity
            groupByFields={[
              { id: 'category', label: 'Category', indexName: 'traffic' },
              { id: 'subcategory', label: 'Subcategory', indexName: 'traffic' }
            ]}
          />
        )}

        {/* Branded/ Unbranded SpineChart Traffic */}
        {(isCategoryEntity || isSubcategoryEntity || isSegmentEntity || isBusinessUnitEntity) &&
          shouldShowBrandUnbranded() && <AtlasV2TotalTrafficV2BrandedUnbrandedSplineChart fieldName="organicClicks" />}

        {(isCategoryEntity || isSegmentEntity || isBusinessUnitEntity) && (
          <AtlasV2FilterableStackedBarChart
            metricDropdownOptions={[
              {
                id: 'organicTraffic',
                label: 'Organic Traffic',
                apiMetricFieldName: 'organicClicks',
                subtitleLabel: 'organic traffic'
              },
              {
                id: 'percentTraffic',
                label: '% of Organic  Traffic',
                apiMetricFieldName: 'organicClicks',
                isPercentageVisible: true,
                defaultMetric: 'organicTraffic',
                subtitleLabel: 'percentage of organic traffic'
              }
            ]}
            groupByFieldOptions={[
              ...(isAllCategoriesEntity || isSegmentEntity || isBusinessUnitEntity
                ? [
                    {
                      id: 'category',
                      label: 'Category'
                    }
                  ]
                : []),
              {
                id: 'subcategory',
                label: 'Subcategory'
              }
            ]}
          />
        )}
        <AtlasV2TrafficProductGrid trafficType="organicClicks" />
      </Flex>
    </BasePageContainer>
  );
};
